import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { emptyObject } from 'ui-components/default-props/empty';

import style from './printer-selector.module.less';

const messages = defineMessages({
  workstationOffline: {
    description: 'printModal.workstationOffline',
    defaultMessage: '{workstationName} (Offline)',
    id: 'Cw9DAM',
  },
  workstationNoPrinters: {
    description: 'printModal.workstationNoPrinter',
    defaultMessage: '{workstationName} (No Shared Printers)',
    id: 'P87p4u',
  },
  selectPrinterText: {
    description: 'printModal.selectPrinterText',
    defaultMessage: 'Select Printer...',
    id: 'jQL52O',
  },
});

const combineWorkstationAndPrinterId = (workstationId, printerId) => {
  return `${workstationId}|${printerId}`;
};

const getWorkstationLabel = (workstationName, isOnline, printers, intl) => {
  if (isOnline && printers.length === 0) {
    return intl.formatMessage(messages.workstationNoPrinters, {
      workstationName,
    });
  }
  if (isOnline) {
    return workstationName;
  }
  return intl.formatMessage(messages.workstationOffline, { workstationName });
};

export const PrinterSelector = ({ availablePrinters, selectedPrinter = emptyObject, disabled = false, onPrinterSelected }) => {
  const intl = useIntl();
  const togglerTitle = selectedPrinter.name || <FormattedMessage {...messages.selectPrinterText} />;

  const onSelected = useCallback(
    (value) => {
      const [workstationId, id] = value.split('|');
      const selectedPrinterGroup = availablePrinters.find((group) => group.workstationId === workstationId) || { printers: [] };
      const newSelectedPrinter = selectedPrinterGroup.printers.find((printer) => printer.id === id);
      const firstOnlineWorkstation = availablePrinters.find((group) => group.workstationIsOnline) || { printers: [] };
      const firstOnlinePrinter = firstOnlineWorkstation.printers[0];

      if (!newSelectedPrinter && !firstOnlinePrinter) {
        return;
      }
      onPrinterSelected(newSelectedPrinter || firstOnlinePrinter);
    },
    [availablePrinters, onPrinterSelected],
  );

  return (
    <DropdownMenu className={style['printing-selection']} disabled={disabled} dropdownMenuShouldHaveWidthOfButton fullWidth variant='form'>
      <DropdownMenuToggler>{togglerTitle}</DropdownMenuToggler>
      {availablePrinters.map(({ workstationId, workstationName, workstationIsOnline, printers }) => {
        const label = getWorkstationLabel(workstationName, workstationIsOnline, printers, intl);
        return (
          <DropdownMenuItemGroup key={workstationId} label={label}>
            {printers.map((printer) => (
              <DropdownMenuItem
                key={printer.id}
                id={printer.id}
                onClick={() => onSelected(combineWorkstationAndPrinterId(printer.workstationId, printer.id))}
              >
                {printer.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuItemGroup>
        );
      })}
    </DropdownMenu>
  );
};

const printerPropType = PropTypes.shape({
  id: PropTypes.string,
  workstationId: PropTypes.string,
  name: PropTypes.string,
});

PrinterSelector.propTypes = {
  availablePrinters: PropTypes.arrayOf(
    PropTypes.shape({
      workstationId: PropTypes.string,
      workstationIsOnline: PropTypes.bool,
      workstationName: PropTypes.string,
      printers: PropTypes.arrayOf(printerPropType),
    }),
  ).isRequired,
  selectedPrinter: printerPropType,
  onPrinterSelected: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
